<script lang="ts">
  // Mini-SVG-Chart für Status-Card-Trajektorien. Bewusst klein und
  // zweckmäßig: keine Achsen, keine Tooltips — der Card-Subtitle
  // liefert den Kontext. Eine Linie + optional gefüllte Fläche
  // darunter, gleicher Akzent wie der status-Badge.
  //
  // Auto-skaliert auf [min..max] der Daten plus 10% Puffer, damit
  // ein flacher Verlauf nicht künstlich dramatisch wirkt.

  interface Props {
    values: (number | null)[];
    width?: number;
    height?: number;
    color?: string; // CSS color (eg "currentColor", "var(--color-success)")
    fill?: boolean;
    fluid?: boolean; // wenn true: SVG fuellt Container-Breite (preserveAspectRatio="none")
    gridLines?: number; // gleichmaessig verteilte vertikale Guides (intern, ohne Rand)
    /** Platz der Messgröße in der Diagramm-Palette (REIHE in
     *  lib/farbe/farbe.ts). Damit folgt die Kurve dem Schalter
     *  „Diagramme" (grau · Bereichston · bunt) wie jedes Chart; `color`
     *  bleibt dann unbenutzt. */
    platz?: number;
  }

  let {
    values,
    width = 120,
    height = 32,
    color = "currentColor",
    fill = true,
    fluid = false,
    gridLines = 0,
    platz,
  }: Props = $props();

  // Mit `platz` kommt die Farbe aus der Klasse .messlinie (app.css):
  // grau/Bereichston über --stufe-1, bunt über --reihe-N.
  const farbe = $derived(platz != null ? "currentColor" : color);
  const stil = $derived(
    (platz != null ? `--reihe: var(--reihe-${platz})` : `color: ${color}`) + (fluid ? "; display: block" : ""),
  );

  const valid = $derived(
    values
      .map((v, i) => ({ v, i }))
      .filter((p): p is { v: number; i: number } => p.v != null),
  );

  const path = $derived.by(() => {
    if (valid.length < 2) return { line: "", area: "" };
    const min = Math.min(...valid.map((p) => p.v));
    const max = Math.max(...valid.map((p) => p.v));
    const range = max - min || 1;
    const pad = range * 0.1;
    const lo = min - pad;
    const hi = max + pad;
    const span = hi - lo;
    const n = values.length;
    const x = (i: number) => (i / Math.max(1, n - 1)) * width;
    const y = (v: number) => height - ((v - lo) / span) * height;

    let line = "";
    for (let k = 0; k < valid.length; k++) {
      const p = valid[k];
      line += (k === 0 ? "M" : "L") + x(p.i).toFixed(1) + "," + y(p.v).toFixed(1);
    }
    const first = valid[0];
    const last = valid[valid.length - 1];
    const area =
      `M${x(first.i).toFixed(1)},${height} ` +
      line.slice(1) +
      ` L${x(last.i).toFixed(1)},${height} Z`;
    return { line, area };
  });
</script>

{#if valid.length >= 2}
  <svg
    width={fluid ? "100%" : width}
    {height}
    viewBox="0 0 {width} {height}"
    preserveAspectRatio="none"
    aria-hidden="true"
    class={platz != null ? "messlinie" : undefined}
    style={stil}
  >
    {#if gridLines > 0}
      {#each Array(gridLines) as _, i}
        {@const gx = ((i + 1) / (gridLines + 1)) * width}
        <line
          x1={gx}
          y1={0}
          x2={gx}
          y2={height}
          stroke="currentColor"
          stroke-opacity="0.18"
          stroke-width="1"
          stroke-dasharray="2 2"
          vector-effect="non-scaling-stroke"
        />
      {/each}
    {/if}
    {#if fill}
      <path d={path.area} fill={farbe} fill-opacity="0.15" />
    {/if}
    <path
      d={path.line}
      fill="none"
      stroke={farbe}
      stroke-width="1.25"
      stroke-linejoin="round"
      stroke-linecap="round"
      vector-effect="non-scaling-stroke"
    />
  </svg>
{/if}
